import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { searchJobs } from '../services/api';
import { JobSearchResult } from '../types';

/**
 * Drives the global free-text job search behind the header search box.
 *
 * Encapsulates the integration's UX rules (see the backend FE guide §6/§8):
 *  - debounce input (~300ms) so we don't fire on every keystroke;
 *  - only call when the trimmed query is >= 2 chars (the backend short-circuits
 *    shorter queries to an empty set, so we mirror that and skip the call);
 *  - abort the in-flight request whenever the query changes or the hook unmounts;
 *  - drop stale / out-of-order responses using a request sequence number AND the
 *    echoed `query` field, so a slow earlier response can never overwrite a newer
 *    one.
 *
 * The caller owns the raw input string; this hook owns the request lifecycle.
 */

export type JobSearchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; query: string; results: JobSearchResult[]; count: number; total: number }
  | { status: 'error' };

export interface UseJobSearchOptions {
  debounceMs?: number;
  minChars?: number;
  limit?: number;
}

const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_MIN_CHARS = 2;
const DEFAULT_LIMIT = 8;

export interface UseJobSearch {
  state: JobSearchState;
  /** Re-run the current query — used by the error state's "Try again". */
  retry: () => void;
}

export function useJobSearch(
  rawQuery: string,
  options: UseJobSearchOptions = {}
): UseJobSearch {
  const {
    debounceMs = DEFAULT_DEBOUNCE_MS,
    minChars = DEFAULT_MIN_CHARS,
    limit = DEFAULT_LIMIT,
  } = options;

  const [debounced, setDebounced] = useState('');
  const [retryToken, setRetryToken] = useState(0);
  const [state, setState] = useState<JobSearchState>({ status: 'idle' });

  // Debounce the raw input into `debounced`.
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(rawQuery), debounceMs);
    return () => clearTimeout(handle);
  }, [rawQuery, debounceMs]);

  // Monotonic request id: only the response matching the latest id may win.
  const seqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const q = debounced.trim();

    // Any new query supersedes whatever is in flight.
    abortRef.current?.abort();

    if (q.length < minChars) {
      // Bump the sequence so a pending longer-query response can't land after
      // the user has shortened/cleared the input.
      seqRef.current += 1;
      setState({ status: 'idle' });
      return;
    }

    const mySeq = ++seqRef.current;
    const controller = new AbortController();
    abortRef.current = controller;

    setState({ status: 'loading' });

    searchJobs({ q, skip: 0, limit }, controller.signal)
      .then((res) => {
        // Stale guard: ignore unless this is still the latest request and the
        // backend echoed back the exact query we asked for.
        if (mySeq !== seqRef.current) return;
        if (res.query.trim() !== q) return;
        setState({
          status: 'success',
          query: res.query,
          results: res.results,
          count: res.count,
          total: res.total,
        });
      })
      .catch((err) => {
        if (axios.isCancel(err) || controller.signal.aborted) return;
        if (mySeq !== seqRef.current) return;
        setState({ status: 'error' });
      });

    return () => controller.abort();
  }, [debounced, retryToken, minChars, limit]);

  const retry = () => setRetryToken((t) => t + 1);

  return { state, retry };
}
